// TODO: Move insurance-related APIs to AdviserHubInsuranceClient in a future refactor.
import { InsuredPersonDetails } from '@/interfaces/insured-person-details/InsuredPersonDetails';
import RequestBuilder from './RequestBuilder';
import { InsuranceLinkInfo } from '@/interfaces/InsuranceLinkInfo';
import { PostPaymentHistoryListRequest } from '@/interfaces/clients/PostPaymentHistoryListRequest';
import { SharePointDocumentsRequest } from '@/interfaces/clients/SharePointDocumentsRequest';
import { ExpiringPoliciesRequest } from "@/interfaces/insured-person-details/UpcomingRenewals";

export default class AdviserHubClient {
  static benefitUrl = 'insurance/benefit';
  static consentUrl = 'insurance/consent';
  static insuredUrl = 'insurance/insured';
  static emailUrl = 'email';
  static insuranceUrl = 'insurance';
  static documentUrl = 'document';
  static sharepointUrl = 'sharepoint';
  static applicationUrl = 'insurance/application';
  static expiringPolicies = 'insurance/policy/expiringpolicies';
  static calculateRenewal = 'insurance/policy/calculate-renewal';
  static exportHtml = 'document/exporthtml';

  static async calculateBenefitPremiums(request: any) {
    return await RequestBuilder.makeGetRequest(
      `${this.benefitUrl}/calculatepremiums`,
      request
    );
  }

  static async checkInsuredPerson(insuredPersonDetails: InsuredPersonDetails) {
    return await RequestBuilder.makePostRequest(
      `${this.insuredUrl}/check`,
      insuredPersonDetails
    );
  }

  static async getConsentQuestions(groupName: string) {
    return await RequestBuilder.makeGetRequest(
      `${this.consentUrl}/group/${groupName}`
    );
  }

  static async getInsuranceApplicationList(request: any) {
    return await RequestBuilder.makePostRequest(
      `${this.applicationUrl}/list`,
      request
    );
  }

  static async getInsurancePoliciesList(request: any) {
    return await RequestBuilder.makePostRequest(
      `${this.insuranceUrl}/policy/list`,
      request
    );
  }

  static async postSendContactUsEmail(request: any) {
    return await RequestBuilder.makePostRequest(
      `${this.emailUrl}/sendcontactusemail/${request.policyId}`,
      request
    );
  }

  static async cancelApplication(request: any) {
    return await RequestBuilder.makePostRequest(
      `${this.applicationUrl}/cancel`,
      request
    );
  }

  static async getInsuranceApplicationDetails(policyId: string) {
    return await RequestBuilder.makeGetRequest(
      `${this.applicationUrl}?policyId=${policyId}`
    );
  }

  static async getInsurancePolicyDetails(policyId: string) {
    return await RequestBuilder.makeGetRequest(
      `${this.insuranceUrl}/policy/${policyId}`
    );
  }

  static async getInsurancePolicyPaymentHistory(
    request: PostPaymentHistoryListRequest
  ) {
    const query = `policyId=${request.policyId}&paymentDate=${
      request.paymentDate
    }&pageSize=${request.pageSize.toString()}&pageNumber=${request.pageNumber.toString()}&sortDirection=${
      request.sortDirection
    }`;
    return await RequestBuilder.makeGetRequest(
      `${this.insuranceUrl}/policy/paymenthistory?${query}`
    );
  }

  static async getExistingApplicationLink(policyId: string) {
    return await RequestBuilder.makeGetRequest(
      `${this.insuranceUrl}/resumelink?policyId=${policyId}`
    );
  }

  static async getSharePointDocuments(request: SharePointDocumentsRequest) {
    let query = `productFolder=${request.productFolder}`;
    if (request.folder) {
      query += `folder=${request.folder}`;
    }
    return await RequestBuilder.makeGetRequest(
      `${this.sharepointUrl}/list?${query}`
    );
  }

  static async getInsuranceLink(insuranceLinkInfo: InsuranceLinkInfo) {
    const params: Record<string, string> = {
      smokerStatus: insuranceLinkInfo.smokerStatus,
      'sumInsured.life': insuranceLinkInfo.sumInsured.life.toString(),
      'sumInsured.tpd': insuranceLinkInfo.sumInsured.tpd.toString(),
      'sumInsured.trauma': insuranceLinkInfo.sumInsured.trauma.toString(),
      applyingForMore: insuranceLinkInfo.applyingForMore.toString(),
      'insuredPerson.firstName': insuranceLinkInfo.insuredPerson.firstName,
      'insuredPerson.middleName': insuranceLinkInfo.insuredPerson.middleName,
      'insuredPerson.lastName': insuranceLinkInfo.insuredPerson.lastName,
      'insuredPerson.email': insuranceLinkInfo.insuredPerson.email,
      'insuredPerson.gender': insuranceLinkInfo.insuredPerson.gender,
      'insuredPerson.dateOfBirth': insuranceLinkInfo.insuredPerson.dateOfBirth,
    };

    return await RequestBuilder.makeGetRequest(
      `${this.insuranceUrl}/link`,
      params
    );
  }

  static async downloadDocument(fileName: string) {
    return await RequestBuilder.makeGetRequest(
      `${this.documentUrl}/download?documentId=${fileName}`
    );
  }

  static async downloadFileFromStream(
    fileName: string,
    contentStream: { content: string; contentType: string }
  ) {
    const byteCharacters = atob(contentStream.content);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: contentStream.contentType });
    const url = URL.createObjectURL(blob);
    const anchorElement = document.createElement('a');
    anchorElement.href = url;
    anchorElement.download = fileName;
    anchorElement.click();
    anchorElement.remove();
    URL.revokeObjectURL(url);
  }

  static flattenObject(obj: any) {
    const result: any = {};
    for (const key of Object.keys(obj)) {
      if (typeof obj[key] === 'object') {
        const nested: any = this.flattenObject(obj[key]);
        for (const nestedKey of Object.keys(nested)) {
          result[`${key}.${nestedKey}`] = nested[nestedKey];
        }
      } else {
        result[key] = obj[key];
      }
    }
    return result;
  }

  static async updateBenefitStatus(request: any) {
    return await RequestBuilder.makePostRequest(
      `${this.applicationUrl}/benefitStatus`,
      request
    );
  }

  static async getExpiringPoliciesInfo(payload: ExpiringPoliciesRequest) {
    const queryParams = new URLSearchParams(payload as Record<string, any>).toString();
    return await RequestBuilder.makeGetRequest(
      `${this.expiringPolicies}?${queryParams}`
    );
  }

  static async getCalculateRenewalInfo(request: any) {
    return await RequestBuilder.makePostRequest(
      `${this.calculateRenewal}`,
      request
    );
  }

  static async exportHtmlPoliciesInfo(request: any, config = {}) {
    return await RequestBuilder.makePostRequest(
      `${this.exportHtml}`,
      request,
      config
    );
  }

  static async exportUpcomingRenewalPoliciesList(request: any) {
  return await RequestBuilder.makePostRequest(
    `${this.documentUrl}/generateupcomingrenewalsdoc`,
    request,
    {
      responseType: "arraybuffer",
    }
  );
}


  static formatDate(dateStr: string) {
    const month = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const date = new Date(dateStr);
    const minDate = new Date("0001-01-01");

    if (dateStr && !this.areDatesEqual(date, minDate)) {
      return date.getDate() + " " + month[date.getMonth()] + " " + date.getFullYear();
    } else {
      return "";
    }
  }

  static areDatesEqual(date1: Date, date2: Date) {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }
}
